import React, { useEffect, useState } from 'react';
import { Form, Input, Switch, Button, Card, message, Tabs, Spin, Space, Select, Radio, Row, Col, Divider } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import RichTextEditor from '../../components/common/RichTextEditor';

const { TextArea } = Input;

interface PageTemplate {
    id: string;
    name: string;
    fieldsSchema: string; // JSON string
}

export const PageEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [useCustomDesign, setUseCustomDesign] = useState(true);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

    const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

    // Load templates
    const { data: templates } = useQuery({
        queryKey: ['page-templates'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/page-templates');
            return response.data.data as PageTemplate[];
        }
    });

    const { data: pageData, isLoading: isLoadingPage } = useQuery({
        queryKey: ['adminPage', id],
        queryFn: async () => {
            const response = await apiClient.get(`/admin/pages/${id}`);
            return response.data.data;
        },
        enabled: isEditMode
    });

    useEffect(() => {
        if (pageData) {
            setUseCustomDesign(pageData.useCustomDesign);
            setSelectedTemplateId(pageData.pageTemplateId);

            const formValues: any = {
                slug: pageData.slug ?? pageData.Slug,
                isActive: pageData.isActive ?? pageData.IsActive,
                useCustomDesign: pageData.useCustomDesign ?? pageData.UseCustomDesign,
                pageTemplateId: pageData.pageTemplateId ?? pageData.PageTemplateId
            };

            const translations = pageData.translations || pageData.Translations || [];
            appLanguages.forEach(lang => {
                const trans = translations.find((t: any) =>
                    (t.languageCode || t.LanguageCode)?.toLowerCase() === lang.toLowerCase()
                );
                let templateDataObj = {};
                const rawTemplateData = trans?.templateData || trans?.TemplateData;
                if (rawTemplateData) {
                    try {
                        templateDataObj = JSON.parse(rawTemplateData);
                    } catch (e) {
                        console.error(`Invalid templateData JSON for language ${lang}`, e);
                    }
                }

                formValues[`translations_${lang}`] = trans ? {
                    title: trans.title || trans.Title || '',
                    htmlContent: trans.htmlContent || trans.HtmlContent || '',
                    metaDescription: trans.metaDescription || trans.MetaDescription || '',
                    metaKeywords: trans.metaKeywords || trans.MetaKeywords || '',
                    templateData: templateDataObj
                } : {
                    title: '',
                    htmlContent: '',
                    metaDescription: '',
                    metaKeywords: '',
                    templateData: {}
                };
            });

            setTimeout(() => {
                form.setFieldsValue(formValues);
            }, 0);
        } else if (!isEditMode) {
            form.setFieldsValue({ isActive: true, useCustomDesign: true });
        }
    }, [pageData, form, isEditMode]);

    const saveMutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = appLanguages.map(lang => {
                const t = values[`translations_${lang}`];
                // Check if title exists (it should if it was loaded or entered)
                if (!t || !t.title) return null;

                const templateDataJson = t.templateData ? JSON.stringify(t.templateData) : null;

                return {
                    languageCode: lang,
                    title: t.title,
                    htmlContent: t.htmlContent || '',
                    metaDescription: t.metaDescription || null,
                    metaKeywords: t.metaKeywords || null,
                    templateData: templateDataJson
                };
            }).filter(Boolean);

            if (translations.length === 0) {
                throw new Error("At least one language translation with a Title is required.");
            }

            const payload = {
                id: isEditMode ? id : undefined,
                slug: values.slug,
                isActive: values.isActive,
                useCustomDesign: values.useCustomDesign,
                pageTemplateId: values.pageTemplateId,
                translations
            };

            if (isEditMode) {
                await apiClient.put(`/admin/pages/${id}`, payload);
            } else {
                await apiClient.post('/admin/pages', payload);
            }
        },
        onSuccess: () => {
            message.success(`Page ${isEditMode ? 'updated' : 'created'} successfully.`);
            queryClient.invalidateQueries({ queryKey: ['adminPages'] });
            navigate('/admin/pages');
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || error.message || 'Failed to save page.');
        }
    });

    if (isEditMode && isLoadingPage) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    // Determine current schema if template is used
    let schemaFields: any[] = [];
    if (useCustomDesign && selectedTemplateId && templates) {
        const template = templates.find(t => t.id === selectedTemplateId);
        if (template) {
            try {
                schemaFields = JSON.parse(template.fieldsSchema);
            } catch (e) {
                console.error("Invalid fieldsSchema JSON", e);
            }
        }
    }

    const tabItems = appLanguages.map(lang => ({
        key: lang,
        label: lang.toUpperCase(),
        children: (
            <div style={{ padding: '20px 0' }}>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name={[`translations_${lang}`, 'title']}
                            label="Page Title"
                            rules={[{ required: true, message: 'Title is required' }]}
                        >
                            <Input size="large" placeholder={`Title in ${lang.toUpperCase()}`} />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider />

                <Row gutter={24}>
                    <Col span={24}>
                        {!useCustomDesign ? (
                            <Form.Item
                                name={[`translations_${lang}`, 'htmlContent']}
                                label="Content (Rich Text Editor)"
                            >
                                <RichTextEditor
                                    key={`editor_${lang}_${pageData?.id || 'new'}`}
                                    placeholder="Enter page content..."
                                    height={400}
                                />
                            </Form.Item>
                        ) : (
                            <Card title="Template Design Fields" size="small" style={{ background: '#fafafa', marginBottom: 24 }}>
                                {schemaFields.length > 0 ? (
                                    <Row gutter={[16, 16]}>
                                        {schemaFields.map(field => (
                                            <Col span={24} key={field.key}>
                                                {field.type === 'richtext' ? (
                                                    <Form.Item name={[`translations_${lang}`, 'templateData', field.key]} label={field.label}>
                                                        <RichTextEditor
                                                            height={200}
                                                        />
                                                    </Form.Item>
                                                ) : field.type === 'image' ? (
                                                    <Form.Item name={[`translations_${lang}`, 'templateData', field.key]} label={`${field.label} (Image URL)`}>
                                                        <Input placeholder="/api/media/..." />
                                                    </Form.Item>
                                                ) : (
                                                    <Form.Item name={[`translations_${lang}`, 'templateData', field.key]} label={field.label}>
                                                        <Input />
                                                    </Form.Item>
                                                )}
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
                                        Please select a template above to see the editable fields.
                                    </div>
                                )}
                            </Card>
                        )}
                    </Col>
                </Row>

                <Divider>SEO Settings</Divider>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name={[`translations_${lang}`, 'metaDescription']} label="Meta Description">
                            <TextArea rows={4} placeholder="Description for search engines..." />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name={[`translations_${lang}`, 'metaKeywords']} label="Meta Keywords">
                            <TextArea rows={4} placeholder="Keywords separated by commas..." />
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        )
    }));

    return (
        <Card
            title={isEditMode ? 'Edit Page' : 'Create New Page'}
            extra={<Button onClick={() => navigate('/admin/pages')}>Back to List</Button>}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={() => {
                    const allValues = form.getFieldsValue(true);
                    saveMutation.mutate(allValues);
                }}
                onValuesChange={(changedValues) => {
                    if (changedValues.useCustomDesign !== undefined) {
                        setUseCustomDesign(changedValues.useCustomDesign);
                    }
                    if (changedValues.pageTemplateId !== undefined) {
                        setSelectedTemplateId(changedValues.pageTemplateId);
                    }
                }}
            >
                <div style={{ background: '#f5f5f5', padding: '24px', borderRadius: '8px', marginBottom: '24px' }}>
                    <Row gutter={24}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="slug"
                                label="URL Slug"
                                tooltip="The URL path for this page (e.g. /about-us)"
                                rules={[
                                    { required: true },
                                    { pattern: /^[a-z0-9\-]+$/, message: 'Only lowercase letters, numbers and hyphens' }
                                ]}
                            >
                                <Input size="large" prefix="/" placeholder="e.g. about-us" />
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Item name="isActive" label="Page Status" valuePropName="checked">
                                <Switch
                                    checkedChildren="Active"
                                    unCheckedChildren="Draft"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="useCustomDesign" label="Edit Mode">
                                <Radio.Group value={useCustomDesign} buttonStyle="solid">
                                    <Radio.Button value={false}>Standard</Radio.Button>
                                    <Radio.Button value={true}>Template</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                        {useCustomDesign && (
                            <Col xs={24} md={6}>
                                <Form.Item name="pageTemplateId" label="Choose Template" rules={[{ required: useCustomDesign }]}>
                                    <Select placeholder="Select a template">
                                        {templates?.map(t => (
                                            <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                </div>

                <div className="page-translations-tabs">
                    <Tabs
                        type="card"
                        items={tabItems}
                        style={{ background: '#fff' }}
                    />
                </div>

                <Divider />

                <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space size="middle">
                        <Button size="large" onClick={() => navigate('/admin/pages')}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            htmlType="submit"
                            loading={saveMutation.isPending}
                            style={{ paddingLeft: 40, paddingRight: 40 }}
                        >
                            {isEditMode ? 'Update Page' : 'Create Page'}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default PageEdit;
